import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { COLOR } from "./constants/colors";
import { PrimaryButton, TransparentButton } from "./components/Buttons";
import Header from "./components/Header";
import mascotPeek from "./mascot_peek.png";

export default function LandingPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [email2, setEmail2] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitted2, setSubmitted2] = useState(false);
  const [howItWorksView, setHowItWorksView] = useState<"buyer" | "seller">("buyer");
  const [whyTiliView, setWhyTiliView] = useState<"buyer" | "seller">("buyer");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    // TODO: Add actual waitlist submission logic here
    console.log("Waitlist email:", email);
    setSubmitted(true);
    
    // Reset after 3 seconds
    setTimeout(() => {
      setEmail("");
      setSubmitted(false);
    }, 3000);
  };

  const handleSubmit2 = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email2) return;

    // TODO: Add actual waitlist submission logic here
    console.log("Waitlist email:", email2);
    setSubmitted2(true);
    
    // Reset after 3 seconds
    setTimeout(() => {
      setEmail2("");
      setSubmitted2(false);
    }, 3000);
  };

  const scrollToWaitlist = () => {
    document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: COLOR.bg,
        color: COLOR.text,
        fontFamily: "'Nunito', ui-sans-serif, system-ui",
      }}
    >
      <Header variant="landing" onWaitlistClick={scrollToWaitlist} />

      {/* Hero Section */}
      <section className="relative min-h-[calc(100vh-80px)] flex items-center justify-center overflow-hidden">
        {/* Background gradient orbs */}
        <div 
          className="hidden md:block absolute top-20 right-10 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse"
          style={{ backgroundColor: COLOR.main }}
        />
        <div 
          className="hidden md:block absolute bottom-20 left-10 w-80 h-80 rounded-full blur-3xl opacity-15 animate-pulse"
          style={{ backgroundColor: COLOR.accent, animationDelay: "2s" }}
        />
        <div 
          className="hidden md:block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl opacity-10"
          style={{ background: "linear-gradient(135deg, #FA674F 0%, #FFD4B3 100%)" }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            {/* Main headline */}
            <h1 className="text-3xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-4 md:mb-6 animate-fade-in">
              <span className="gradient-ombre-pulse">
                Discover local finds.
              </span>
              <br />
              <span>
                Move out effortlessly.
              </span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-sm md:text-xl mb-12 md:mb-16 max-w-3xl mx-auto leading-relaxed" style={{ color: COLOR.subtext }}>
              Tili connects Seattle neighbors through in-home sales — helping buyers
              uncover great deals nearby, and helping sellers clear out their place in one go.
            </p>

            {/* Two cards side by side */}
            <div className="max-w-6xl mx-auto grid md:grid-cols-[1.5fr,1fr] gap-4 md:gap-6 relative">
              {/* Waitlist card */}
              <div
                className="p-4 md:p-5 rounded-3xl border shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col relative"
                id="waitlist"
                style={{
                  backgroundColor: "#fff",
                  borderColor: COLOR.border,
                }}
              >
                {/* Peeking mascot */}
                <img 
                  src={mascotPeek} 
                  alt="Tili mascot" 
                  className="absolute -top-[42px] md:-top-[63px] left-4 md:left-8 w-16 h-16 md:w-24 md:h-24 object-contain pointer-events-none z-10"
                />
                
                <div className="font-extrabold text-sm md:text-base mb-1.5 md:mb-2">Join the early access list</div>
                <p className="text-xs md:text-sm mb-3 md:mb-4 leading-snug flex-grow" style={{ color: COLOR.subtext }}>
                  Be the first to shop or host a Tili home sale when we launch in Seattle.
                </p>

                {submitted ? (
                  <div
                    className="py-1.5 px-3 md:py-2 md:px-4 rounded-xl text-center text-xs md:text-sm font-extrabold"
                    style={{
                      backgroundColor: COLOR.accent,
                      color: COLOR.text,
                    }}
                  >
                    ✓ You're on the list!
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex gap-1.5 md:gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="flex-1 px-2.5 py-1.5 md:px-3 md:py-2 rounded-xl text-xs md:text-sm outline-none border"
                      style={{
                        borderColor: COLOR.border,
                        backgroundColor: COLOR.bg,
                      }}
                      onFocus={(e) => e.target.style.borderColor = COLOR.main}
                      onBlur={(e) => e.target.style.borderColor = COLOR.border}
                    />
                    <PrimaryButton type="submit" className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm">
                      Join
                    </PrimaryButton>
                  </form>
                )}
              </div>

              {/* Listing preview card */}
              <div
                className="p-4 md:p-5 rounded-3xl border shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group h-full flex flex-col"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate("/listing");
                }}
                style={{
                  backgroundColor: "#fff",
                  borderColor: COLOR.border,
                }}
              >
                <div className="font-extrabold text-sm md:text-base mb-1.5 md:mb-2">See a Tili home sale</div>
                <p className="text-xs md:text-sm mb-3 md:mb-4 leading-snug flex-grow" style={{ color: COLOR.subtext }}>
                  One of Tili's founders is moving out Dec 3!
                </p>
                
                <PrimaryButton className="w-full py-1.5 md:py-2 text-xs md:text-sm">
                  View listing →
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how" className="py-12 md:py-20" style={{ backgroundColor: "rgba(250, 103, 79, 0.03)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-extrabold mb-2 md:mb-3">How Tili works</h2>
            <p className="text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-6 md:mb-8" style={{ color: COLOR.subtext }}>
              In-home sales help buyers explore everything at once — and help sellers
              avoid endless listings, messages, and meetups.
            </p>
            
            {/* Switch Control */}
            <div className="inline-flex rounded-full p-1" style={{ backgroundColor: COLOR.border }}>
              <button
                onClick={() => setHowItWorksView("buyer")}
                className="px-6 py-2 rounded-full text-sm font-extrabold transition-all duration-200"
                style={{
                  backgroundColor: howItWorksView === "buyer" ? "#fff" : "transparent",
                  color: howItWorksView === "buyer" ? COLOR.text : COLOR.subtext,
                }}
              >
                Buyer
              </button>
              <button
                onClick={() => setHowItWorksView("seller")}
                className="px-6 py-2 rounded-full text-sm font-extrabold transition-all duration-200"
                style={{
                  backgroundColor: howItWorksView === "seller" ? "#fff" : "transparent",
                  color: howItWorksView === "seller" ? COLOR.text : COLOR.subtext,
                }}
              >
                Seller
              </button>
            </div>
          </div>

          <div className="relative">
            {/* Horizontal lines between icons */}
            <div className="hidden md:flex absolute top-[2rem] left-0 right-0 items-center justify-center px-16">
              <div className="flex-1 max-w-[200px]"></div>
              <div className="h-px flex-1 opacity-30" style={{ backgroundColor: COLOR.subtext }}></div>
              <div className="flex-1 max-w-[200px]"></div>
              <div className="h-px flex-1 opacity-30" style={{ backgroundColor: COLOR.subtext }}></div>
              <div className="flex-1 max-w-[200px]"></div>
            </div>

            {howItWorksView === "buyer" ? (
              <div className="grid md:grid-cols-3 gap-8 relative">
                {/* Step 1 */}
                <div className="text-center">
                  <div className="flex flex-col items-center mb-6">
                    <div className="text-4xl mb-1">🔍</div>
                    <div className="text-xs font-extrabold" style={{ color: COLOR.subtext }}>STEP 1</div>
                  </div>
                  <div className="font-extrabold text-lg md:text-xl mb-2 md:mb-3">Discover sales nearby</div>
                  <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                    Browse curated Tili sales in your neighborhood and across Seattle, complete with photos, item previews, and available time slots.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="text-center">
                  <div className="flex flex-col items-center mb-6">
                    <div className="text-4xl mb-1">📅</div>
                    <div className="text-xs font-extrabold" style={{ color: COLOR.subtext }}>STEP 2</div>
                  </div>
                  <div className="font-extrabold text-lg md:text-xl mb-2 md:mb-3">Book a visit</div>
                  <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                    Reserve a time slot to explore the seller's home sale. No back-and-forth DMs or random meetups.
                  </p>
                </div>

                {/* Step 3 */}
                <div className="text-center">
                  <div className="flex flex-col items-center mb-6">
                    <div className="text-4xl mb-1">🎉</div>
                    <div className="text-xs font-extrabold" style={{ color: COLOR.subtext }}>STEP 3</div>
                  </div>
                  <div className="font-extrabold text-lg md:text-xl mb-2 md:mb-3">Shop in one go</div>
                  <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                    Buyers negotiate and buy in person. Sellers offload everything fast in a single sale.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8 relative">
                {/* Step 1 */}
                <div className="text-center">
                  <div className="flex flex-col items-center mb-6">
                    <div className="text-4xl mb-1">🔧</div>
                    <div className="text-xs font-extrabold" style={{ color: COLOR.subtext }}>STEP 1</div>
                  </div>
                  <div className="font-extrabold text-lg md:text-xl mb-2 md:mb-3">Create your Tili sale</div>
                  <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                    Tell us what you're selling and add a few photos. Tili turns your place into a single sale event — no item-by-item posting required.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="text-center">
                  <div className="flex flex-col items-center mb-6">
                    <div className="text-4xl mb-1">🗓️</div>
                    <div className="text-xs font-extrabold" style={{ color: COLOR.subtext }}>STEP 2</div>
                  </div>
                  <div className="font-extrabold text-lg md:text-xl mb-2 md:mb-3">Set your time slots</div>
                  <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                    Choose when buyers can visit. Only verified buyers can book, keeping everything simple and safe.
                  </p>
                </div>

                {/* Step 3 */}
                <div className="text-center">
                  <div className="flex flex-col items-center mb-6">
                    <div className="text-4xl mb-1">💰</div>
                    <div className="text-xs font-extrabold" style={{ color: COLOR.subtext }}>STEP 3</div>
                  </div>
                  <div className="font-extrabold text-lg md:text-xl mb-2 md:mb-3">Sell everything in one go</div>
                  <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                    Buyers browse your home sale all at once and negotiate in person — so you clear out most of your items in a single event.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Tili Section */}
      <section id="values" className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-extrabold mb-2 md:mb-3">Why Tili</h2>
            <p className="text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-6 md:mb-8" style={{ color: COLOR.subtext }}>
              Built for urban living — safer than random meetups, easier than selling item by item, and made for Seattle neighborhoods.
            </p>
            
            {/* Switch Control */}
            <div className="inline-flex rounded-full p-1" style={{ backgroundColor: COLOR.border }}>
              <button
                onClick={() => setWhyTiliView("buyer")}
                className="px-6 py-2 rounded-full text-sm font-extrabold transition-all duration-200"
                style={{
                  backgroundColor: whyTiliView === "buyer" ? "#fff" : "transparent",
                  color: whyTiliView === "buyer" ? COLOR.text : COLOR.subtext,
                }}
              >
                Buyer
              </button>
              <button
                onClick={() => setWhyTiliView("seller")}
                className="px-6 py-2 rounded-full text-sm font-extrabold transition-all duration-200"
                style={{
                  backgroundColor: whyTiliView === "seller" ? "#fff" : "transparent",
                  color: whyTiliView === "seller" ? COLOR.text : COLOR.subtext,
                }}
              >
                Seller
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            <div
              className="p-5 md:p-7 rounded-3xl border shadow-lg hover:shadow-xl transition-all duration-300 group"
              style={{
                backgroundColor: "#fff",
                borderColor: COLOR.border,
              }}
            >
              <span className="text-4xl md:text-5xl inline-block mb-4 md:mb-5 group-hover:scale-110 transition-transform duration-300">💎</span>
              <div className="font-extrabold text-lg md:text-xl mb-3 md:mb-4">Local treasure hunting</div>
              {whyTiliView === "buyer" ? (
                <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                  Find unique, high-quality items for a fraction of retail — right next door.
                </p>
              ) : (
                <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                  Give your belongings a great new home and make quick cash without the hassle.
                </p>
              )}
            </div>

            <div
              className="p-5 md:p-7 rounded-3xl border shadow-lg hover:shadow-xl transition-all duration-300 group"
              style={{
                backgroundColor: "#fff",
                borderColor: COLOR.border,
              }}
            >
              <span className="text-4xl md:text-5xl inline-block mb-4 md:mb-5 group-hover:scale-110 transition-transform duration-300">⚡</span>
              <div className="font-extrabold text-lg md:text-xl mb-3 md:mb-4">The one-visit marketplace</div>
              {whyTiliView === "buyer" ? (
                <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                  Explore everything at once — no back-and-forth messaging.
                </p>
              ) : (
                <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                  Sell most of your things in one event and skip 100+ "Is this available?" DMs.
                </p>
              )}
            </div>

            <div
              className="p-5 md:p-7 rounded-3xl border shadow-lg hover:shadow-xl transition-all duration-300 group"
              style={{
                backgroundColor: "#fff",
                borderColor: COLOR.border,
              }}
            >
              <span className="text-4xl md:text-5xl inline-block mb-4 md:mb-5 group-hover:scale-110 transition-transform duration-300">🔒</span>
              <div className="font-extrabold text-lg md:text-xl mb-3 md:mb-4">Safe & screened connections</div>
              {whyTiliView === "buyer" ? (
                <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                  Book visits confidently — everyone is verified via LinkedIn or workplace/school.
                </p>
              ) : (
                <p className="text-xs md:text-sm leading-relaxed" style={{ color: COLOR.subtext }}>
                  Your address stays protected — only verified buyers see it when they are confirmed a time slot.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-12 md:py-20" style={{ backgroundColor: "rgba(250, 103, 79, 0.03)" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div>
              <h4
                className="text-xs font-extrabold uppercase mb-3 md:mb-4 tracking-wider"
                style={{ color: COLOR.main }}
              >
                Vision
              </h4>
              <div
                className="p-6 md:p-8 rounded-3xl border shadow-lg"
                style={{
                  backgroundColor: "#fff",
                  borderColor: COLOR.border,
                }}
              >
                <p className="text-base md:text-lg leading-relaxed font-medium">
                  A world where great local finds are easy to discover, and moving out feels
                  effortless.
                </p>
              </div>
            </div>

            <div>
              <h4
                className="text-xs font-extrabold uppercase mb-3 md:mb-4 tracking-wider"
                style={{ color: COLOR.main }}
              >
                Mission
              </h4>
              <div
                className="p-6 md:p-8 rounded-3xl border shadow-lg"
                style={{
                  backgroundColor: "#fff",
                  borderColor: COLOR.border,
                }}
              >
                <p className="text-base md:text-lg leading-relaxed font-medium">
                  To connect neighbors through safe in-home sales that make buying and
                  selling simple, fast, and hassle-free.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div
            className="p-12 md:p-16 rounded-3xl border shadow-xl relative overflow-hidden"
            style={{
              backgroundColor: "#fff",
              borderColor: COLOR.border,
            }}
          >
            {/* Decorative gradient orbs */}
            <div 
              className="absolute top-0 right-0 w-80 h-80 rounded-full blur-3xl opacity-10"
              style={{ background: COLOR.main }}
            />
            <div 
              className="absolute bottom-0 left-0 w-64 h-64 rounded-full blur-3xl opacity-10"
              style={{ background: COLOR.accent }}
            />
            
            <div className="max-w-3xl mx-auto text-center relative z-10">
              <h3 className="text-2xl md:text-3xl font-extrabold mb-4 leading-tight md:whitespace-nowrap">
                Be first in line when Tili launches in Seattle.
              </h3>
              <p className="text-base md:text-lg mb-8 leading-relaxed" style={{ color: COLOR.subtext }}>
                Join the waitlist to get early access as a buyer or seller.
              </p>

              {submitted2 ? (
                <div
                  className="py-2 px-4 rounded-xl text-center text-sm font-extrabold inline-block"
                  style={{
                    backgroundColor: COLOR.accent,
                    color: COLOR.text,
                  }}
                >
                  ✓ You're on the list!
                </div>
              ) : (
                <form onSubmit={handleSubmit2} className="flex flex-col sm:flex-row gap-2 max-w-xl mx-auto">
                  <input
                    type="email"
                    value={email2}
                    onChange={(e) => setEmail2(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="flex-1 px-3 py-2 rounded-xl text-sm outline-none border"
                    style={{
                      borderColor: COLOR.border,
                      backgroundColor: COLOR.bg,
                    }}
                    onFocus={(e) => e.target.style.borderColor = COLOR.main}
                    onBlur={(e) => e.target.style.borderColor = COLOR.border}
                  />
                  <PrimaryButton type="submit" className="px-4 py-2 text-sm whitespace-nowrap">
                    Join waitlist
                  </PrimaryButton>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-8 text-center"
        style={{
          borderTop: `1px solid ${COLOR.border}`,
          backgroundColor: "rgba(250, 103, 79, 0.02)",
        }}
      >
        <div className="mb-2">
          <span className="text-2xl">🦔</span>
        </div>
        <p className="text-sm font-bold mb-1" style={{ color: COLOR.text }}>
          © 2025 Tili
        </p>
        <p className="text-xs font-semibold" style={{ color: COLOR.subtext }}>
          Built with ❤️ in Seattle for Seattle
        </p>
      </footer>
    </div>
  );
}
